import React, { useRef, useState } from "react";
import { Camera, Loader2, X } from "lucide-react";
import { useLanguage, useT } from "@/lib/LanguageContext";
// Relative on purpose: pure modules, resolvable under Jest without a shim.
import { resolveApiUrl } from "../../pathways/learnerApi";
import { hasSubmissionContent, PHOTO_TYPES, photoProblem } from "../../pathways/learnerModel";
import { fmtInstantDate } from "../../pathways/format";
import { pathwayErrorMessage } from "../../pathways/errors";
import { PATHWAYS_LEARNER as IDS } from "../../constants/testIds/pathwaysLearner";

const PRIMARY = "inline-flex h-11 items-center justify-center gap-2 rounded-full bg-eh-ink px-6 text-sm font-semibold text-eh-cream hover:bg-eh-ink/90 disabled:cursor-not-allowed disabled:opacity-50";

/** The text box's ceiling, the server's own (contract §10.3, `text` 0..5000). */
const TEXT_MAX = 5000;

const PHOTO_PROBLEM_KEYS = Object.freeze({
  type: "pathways.practice.photoType",
  size: "pathways.practice.photoTooLarge",
});

const STATUS_CLASSES = Object.freeze({
  pending: "bg-eh-ochre/10 text-eh-ink",
  approved: "bg-eh-sage-soft text-eh-ink",
  feedback_sent: "bg-eh-cream-deep text-eh-ink",
  superseded: "bg-eh-cream-deep text-eh-muted",
});
const STATUS_KEYS = Object.freeze({
  pending: "pathways.practice.status.pending",
  approved: "pathways.practice.status.approved",
  feedback_sent: "pathways.practice.status.feedback_sent",
  superseded: "pathways.practice.status.superseded",
});

/** One of the learner's own submissions: when, its review status, the text, the photo, the feedback. */
function SubmissionCard({ submission }) {
  const t = useT("public");
  const { lang } = useLanguage();
  const status = STATUS_KEYS[submission.status] ? submission.status : "pending";
  return (
    <li data-testid={IDS.practiceSubmission(submission.id)} className="space-y-2 rounded-2xl bg-eh-surface px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-eh-muted">
          {t("pathways.practice.submittedOn", { date: fmtInstantDate(submission.submitted_at, lang) })}
        </span>
        <span
          data-testid={IDS.practiceSubmissionStatus(submission.id)}
          data-status={submission.status}
          className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_CLASSES[status]}`}
        >
          {t(STATUS_KEYS[status])}
        </span>
      </div>
      {submission.text && (
        <p className="whitespace-pre-line break-words text-sm text-eh-ink">{submission.text}</p>
      )}
      {submission.photo_url && (
        <img
          src={resolveApiUrl(submission.photo_url)}
          alt={t("pathways.practice.photoAlt")}
          loading="lazy"
          data-testid={IDS.practiceSubmissionPhoto(submission.id)}
          className="max-h-64 w-auto rounded-xl"
        />
      )}
      {submission.feedback && (
        <div data-testid={IDS.practiceSubmissionFeedback(submission.id)} className="rounded-xl bg-eh-cream-deep px-3 py-2">
          <p className="text-xs font-semibold text-eh-ink">{t("pathways.practice.feedback")}</p>
          <p className="mt-1 whitespace-pre-line break-words text-sm text-eh-ink">{submission.feedback}</p>
        </div>
      )}
    </li>
  );
}

/**
 * A practice step's content (contract §10.0.2, §10.3, §10.7), rendered by StepScreenView:
 *
 *   instructions the practitioner's text
 *   completion   `submission_required` false: the caller's `markDone` (the shared
 *                "Mark as done"); true: the submission form below
 *   submissions  the learner's own, listed whenever there are any, so feedback stays
 *                readable even if the practitioner later turned the toggle off
 *
 * The form:
 *   text box     the learner's notes (0..5000)
 *   photo        optional; JPEG, PNG or WebP up to 10 MB, refused HERE before any upload
 *                (learnerModel.photoProblem), so a phone never sends a file the server
 *                would answer 413 / 415 to. The server checks again: this is a courtesy.
 *   Submit       `onSubmit({ text, photo })`; text and photo cannot both be empty. A
 *                success clears the form and says the submission went for review; the
 *                container re-reads the screen, which lists it.
 *   submissions  newest first, as the server sends them (≤ 10): status, text, photo
 *                (API-relative, resolved like every other step url), feedback.
 *
 * The step stays done after feedback, and the form stays: a learner may resubmit
 * (contract §10.0.2).
 *
 * `disabled` (the admin preview): every control is inert and nothing is sent.
 *
 * @param {object} props
 * @param {{ instructions: string, submission_required: boolean, submissions: object[] }} props.practice
 * @param {React.ReactNode} [props.markDone]  shown in place of the form when no submission is required
 * @param {(body: { text: string, photo: File|null }) => Promise<any>} [props.onSubmit]
 * @param {boolean} [props.disabled=false]
 */
export default function StepPractice({ practice, markDone = null, onSubmit, disabled = false }) {
  const t = useT("public");
  const tc = useT("common");
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);
  const submissions = Array.isArray(practice.submissions) ? practice.submissions : [];

  const clearFile = () => {
    setPhoto(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const pickPhoto = (e) => {
    const file = e.target.files && e.target.files[0];
    setSubmitted(false);
    if (!file) {
      setPhoto(null);
      return;
    }
    const problem = photoProblem(file);
    if (problem) {
      clearFile();
      setError(t(PHOTO_PROBLEM_KEYS[problem]));
      return;
    }
    setError("");
    setPhoto(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy || disabled || !onSubmit) return;
    if (!hasSubmissionContent(text, photo)) {
      setError(t("pathways.practice.empty"));
      return;
    }
    setBusy(true);
    setError("");
    setSubmitted(false);
    try {
      await onSubmit({ text, photo });
      setText("");
      clearFile();
      setSubmitted(true);
    } catch (err) {
      setError(pathwayErrorMessage(err, tc, t("pathways.practice.submitFailed")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={IDS.practice} className="space-y-4">
      {practice.instructions && practice.instructions.trim() && (
        <div data-testid={IDS.practiceInstructions} className="rounded-2xl bg-eh-surface px-4 py-4 text-sm text-eh-ink whitespace-pre-line break-words">
          {practice.instructions}
        </div>
      )}

      {!practice.submission_required ? markDone : (
        <form data-testid={IDS.practiceForm} onSubmit={submit} noValidate className="space-y-3">
          <label className="block">
            <span className="text-sm font-semibold text-eh-ink">{t("pathways.practice.textLabel")}</span>
            <textarea
              value={text}
              onChange={(e) => { setText(e.target.value); setSubmitted(false); if (error) setError(""); }}
              maxLength={TEXT_MAX}
              rows={5}
              disabled={disabled}
              data-testid={IDS.practiceText}
              className="mt-1 block w-full rounded-2xl border border-eh-border-soft bg-eh-surface px-4 py-3 text-sm text-eh-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-eh-ink disabled:opacity-60"
            />
          </label>

          <div className="space-y-1">
            <label className={`inline-flex items-center gap-2 rounded-full border border-eh-border px-4 py-2 text-sm text-eh-ink focus-within:ring-2 focus-within:ring-eh-ink ${disabled ? "opacity-60" : "cursor-pointer hover:bg-eh-cream-deep"}`}>
              <Camera className="h-4 w-4" aria-hidden="true" />
              <span>{t("pathways.practice.photoLabel")}</span>
              <input
                ref={fileRef}
                type="file"
                accept={PHOTO_TYPES.join(",")}
                onChange={pickPhoto}
                disabled={disabled}
                data-testid={IDS.practicePhoto}
                className="sr-only"
              />
            </label>
            <p className="text-xs text-eh-muted">{t("pathways.practice.photoHint")}</p>
            {photo && (
              <p className="flex items-center gap-2 text-sm text-eh-ink">
                <span data-testid={IDS.practicePhotoName} className="min-w-0 break-all">{photo.name}</span>
                <button
                  type="button"
                  onClick={clearFile}
                  data-testid={IDS.practicePhotoRemove}
                  className="inline-flex items-center gap-1 text-xs text-eh-muted hover:text-eh-ink"
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("pathways.practice.photoRemove")}
                </button>
              </p>
            )}
          </div>

          {error && <p role="alert" data-testid={IDS.practiceError} className="text-sm text-rose-700">{error}</p>}
          {submitted && (
            <p role="status" data-testid={IDS.practiceSubmitted} className="text-sm text-eh-ink">{t("pathways.practice.submitted")}</p>
          )}

          <button type="submit" disabled={busy || disabled} data-testid={IDS.practiceSubmit} className={`${PRIMARY} w-full`}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {t("pathways.practice.submit")}
          </button>
          {submissions.length > 0 && <p className="text-xs text-eh-muted">{t("pathways.practice.resubmitHint")}</p>}
        </form>
      )}

      {submissions.length > 0 && (
        <section data-testid={IDS.practiceSubmissions} aria-labelledby="pathway-practice-submissions" className="space-y-2">
          <h2 id="pathway-practice-submissions" className="text-sm font-semibold text-eh-ink">{t("pathways.practice.submissions")}</h2>
          <ul className="space-y-2">
            {submissions.map((s) => <SubmissionCard key={s.id} submission={s} />)}
          </ul>
        </section>
      )}
    </div>
  );
}
